import React from 'react';
import { Box, Typography, Breadcrumbs, Link as MuiLink, Button } from '@mui/material';
import { Link } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import { NavigateNext as NavigateNextIcon } from '@mui/icons-material';
import brandingConfig from '../../config/brandingConfig';

const HeaderContainer = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  padding: theme.spacing(2, 0),
  [theme.breakpoints.down('sm')]: {
    marginBottom: theme.spacing(2),
    padding: theme.spacing(1.5, 0),
  },
}));

const TitleRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: theme.spacing(1),
  gap: theme.spacing(1.5),
  [theme.breakpoints.down('sm')]: {
    alignItems: 'stretch',
    flexDirection: 'column',
  },
}));

const PageTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 'bold',
  color: theme.palette.text.primary,
  [theme.breakpoints.down('sm')]: {
    fontSize: '1.35rem',
    lineHeight: 1.25,
  },
}));

const ActionButton = styled(Button)(({ theme }) => ({
  borderRadius: parseInt(brandingConfig.ui.borderRadius),
  backgroundColor: brandingConfig.colors.primary,
  // Der Hintergrund wird hier direkt gesetzt, damit greift MUIs contrastText
  // nicht — die Schriftfarbe muss deshalb selbst aus der Markenhelligkeit
  // abgeleitet werden.
  color: theme.palette.primary.contrastText,
  whiteSpace: 'nowrap',
  '&:hover': {
    backgroundColor: brandingConfig.colors.primary,
    filter: 'brightness(90%)'
  },
  [theme.breakpoints.down('sm')]: {
    flex: '1 1 auto',
    minHeight: 44,
  },
}));

const BreadcrumbLink = styled(MuiLink)(({ theme }) => ({
  color: theme.palette.text.secondary,
  textDecoration: 'none',
  '&:hover': {
    textDecoration: 'underline',
  },
}));

const PageHeader = ({ 
  title, 
  breadcrumbs = [], 
  actionButton = null,
  secondaryButton = null,
  secondaryButtons = null,
  description = null 
}) => {
  const secondaryActions = secondaryButtons || (secondaryButton ? [secondaryButton] : []);

  return (
    <HeaderContainer>
      <TitleRow>
        <PageTitle variant="h4">
          {title}
        </PageTitle>
        <Box
          sx={{
            display: 'flex',
            gap: { xs: 1, sm: 2 },
            flexWrap: 'wrap',
            justifyContent: { xs: 'stretch', sm: 'flex-end' },
          }}
        >
          {secondaryActions.map((button) => (
            <ActionButton 
              key={button.label}
              variant="outlined" 
              color="primary" 
              startIcon={button.icon}
              onClick={button.onClick}
              component={button.path ? Link : undefined}
              to={button.path}
            >
              {button.label}
            </ActionButton>
          ))}
          {actionButton && (
            <ActionButton 
              variant="contained" 
              color="primary" 
              startIcon={actionButton.icon}
              onClick={actionButton.onClick}
              component={actionButton.path ? Link : undefined}
              to={actionButton.path}
            >
              {actionButton.label}
            </ActionButton>
          )}
        </Box>
      </TitleRow>
      
      {breadcrumbs.length > 0 && (
        <Breadcrumbs 
          separator={<NavigateNextIcon fontSize="small" />} 
          aria-label="breadcrumb"
          sx={{ mb: 1, display: { xs: 'none', sm: 'flex' } }}
        >
          {breadcrumbs.map((crumb, index) => {
            const isLast = index === breadcrumbs.length - 1;
            
            return isLast ? (
              <Typography key={index} color="text.primary">
                {crumb.label}
              </Typography>
            ) : (
              <BreadcrumbLink
                key={index}
                component={Link}
                to={crumb.path}
                underline="hover"
              >
                {crumb.label}
              </BreadcrumbLink>
            );
          })}
        </Breadcrumbs>
      )}
      
      {description && (
        <Typography variant="body1" color="text.secondary" sx={{ display: { xs: 'none', sm: 'block' } }}>
          {description}
        </Typography>
      )}
    </HeaderContainer>
  );
};

export default PageHeader;
